import React, { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Download, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { useCrm } from "../../pages/crm/CrmApp";
import { money, QSTATUS, handleWa, preOpen } from "../../lib/crmApi";
import { API } from "../../api";
import { toast } from "../../hooks/use-toast";
import { inputCls, Labeled } from "../market/admin/ui";

const blank = () => ({ description: "", qty: 1, price: 0 });

export const QuoteEditor = ({ open, contact, quote, contacts, onClose, onSaved }) => {
  const { api, me } = useCrm();
  const def = me.org.quote || {};
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(null);
  useEffect(() => {
    if (!open) return;
    setErr("");
    setSaved(quote || null);
    setF(quote ? { ...quote } : { contact_id: contact?.id || "", items: [blank()], discount: 0, tax_pct: def.tax_pct ?? 16, valid_days: def.valid_days ?? 15, notes: "", status: "borrador" });
  }, [open, quote?.id, contact?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!f) return null;
  const setItem = (i, k, v) => setF((x) => ({ ...x, items: x.items.map((it, j) => (j === i ? { ...it, [k]: v } : it)) }));
  const sub = f.items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.price) || 0), 0);
  const base = Math.max(0, sub - (Number(f.discount) || 0));
  const tax = (base * (Number(f.tax_pct) || 0)) / 100;

  const persist = async () => {
    const body = {
      contact_id: f.contact_id, discount: Number(f.discount) || 0, tax_pct: Number(f.tax_pct) || 0, valid_days: Number(f.valid_days) || 15,
      notes: f.notes, status: f.status, items: f.items.filter((it) => it.description.trim()).map((it) => ({ description: it.description, qty: Number(it.qty) || 1, price: Number(it.price) || 0 })),
    };
    const q = await api(saved ? `/quotes/${saved.id}` : "/quotes", { method: saved ? "PUT" : "POST", body });
    setSaved(q);
    setF((x) => ({ ...x, ...q }));
    onSaved?.();
    return q;
  };
  const run = async (kind) => {
    setBusy(kind);
    setErr("");
    const w = kind === "save" ? null : preOpen();
    try {
      const q = await persist();
      if (kind === "pdf") {
        const url = `${API}/crm/q/${q.public_token}.pdf`;
        if (w) w.location.href = url;
        else window.open(url, "_blank");
      }
      if (kind === "wa") {
        toast({ title: handleWa(await api(`/quotes/${q.id}/whatsapp`, { method: "POST", body: { origin_url: window.location.origin } }), w) });
        if (q.status === "borrador") setF((x) => ({ ...x, status: "enviada" }));
        onSaved?.();
      }
      if (kind === "save") toast({ title: `Cotización ${q.number} guardada` });
    } catch (e) {
      w?.close();
      setErr(e.message);
    } finally {
      setBusy("");
    }
  };
  const del = async () => {
    if (!saved || !window.confirm("¿Eliminar esta cotización?")) return;
    await api(`/quotes/${saved.id}`, { method: "DELETE" });
    onSaved?.();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[94vh] max-w-xl overflow-y-auto rounded-2xl" data-testid="crm-quote-editor">
        <DialogHeader><DialogTitle>{saved ? `Cotización ${saved.number}` : "Nueva cotización"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {contacts ? (
            <Labeled label="Cliente">
              <select className={inputCls} value={f.contact_id} onChange={(e) => setF({ ...f, contact_id: e.target.value })} data-testid="crm-quote-contact">
                <option value="">Elige un cliente…</option>
                {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ""}</option>)}
              </select>
            </Labeled>
          ) : (
            <p className="text-sm text-slate-600">Para <b>{contact?.name || f.contact_name}</b></p>
          )}
          <div className="space-y-2">
            {f.items.map((it, i) => (
              <div key={i} className="grid grid-cols-[1fr_56px_90px_32px] gap-1.5" data-testid={`crm-quote-item-${i}`}>
                <input className={inputCls} placeholder="Producto o servicio" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`crm-quote-desc-${i}`} />
                <input type="number" min="0" step="any" className={`${inputCls} px-2`} value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} data-testid={`crm-quote-qty-${i}`} />
                <input type="number" min="0" step="any" className={`${inputCls} px-2`} placeholder="Precio" value={it.price} onChange={(e) => setItem(i, "price", e.target.value)} data-testid={`crm-quote-price-${i}`} />
                <button type="button" onClick={() => setF((x) => ({ ...x, items: x.items.length > 1 ? x.items.filter((_, j) => j !== i) : x.items }))} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <button type="button" onClick={() => setF((x) => ({ ...x, items: [...x.items, blank()] }))} className="inline-flex items-center gap-1 text-sm font-medium" style={{ color: "var(--crm)" }} data-testid="crm-quote-add-item"><Plus className="h-4 w-4" /> Agregar concepto</button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Labeled label="Descuento $"><input type="number" min="0" className={inputCls} value={f.discount} onChange={(e) => setF({ ...f, discount: e.target.value })} data-testid="crm-quote-discount" /></Labeled>
            <Labeled label="IVA %"><input type="number" min="0" className={inputCls} value={f.tax_pct} onChange={(e) => setF({ ...f, tax_pct: e.target.value })} data-testid="crm-quote-tax" /></Labeled>
            <Labeled label="Vigencia días"><input type="number" min="1" className={inputCls} value={f.valid_days} onChange={(e) => setF({ ...f, valid_days: e.target.value })} data-testid="crm-quote-valid" /></Labeled>
          </div>
          <Labeled label="Estado">
            <select className={inputCls} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="crm-quote-status">
              {Object.entries(QSTATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Labeled>
          <Labeled label="Notas para el cliente"><textarea rows={2} className={inputCls} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="crm-quote-notes" /></Labeled>
          <div className="rounded-xl bg-slate-50 p-3 text-sm">
            <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{money(sub)}</span></div>
            {Number(f.discount) > 0 && <div className="flex justify-between text-slate-600"><span>Descuento</span><span data-testid="crm-quote-discount-line">-{money(f.discount)}</span></div>}
            <div className="flex justify-between text-slate-600"><span>IVA</span><span>{money(tax)}</span></div>
            <div className="mt-1 flex justify-between text-base font-bold text-slate-900"><span>Total</span><span data-testid="crm-quote-total">{money(base + tax)}</span></div>
          </div>
          {err && <p className="text-sm text-red-600" data-testid="crm-quote-error">{err}</p>}
          <div className="grid grid-cols-3 gap-2">
            <button disabled={!!busy || !f.contact_id} onClick={() => run("save")} className="flex items-center justify-center gap-1.5 rounded-xl py-3 text-sm font-semibold text-white disabled:opacity-50" style={{ background: "var(--crm)" }} data-testid="crm-quote-save">{busy === "save" && <Loader2 className="h-4 w-4 animate-spin" />} Guardar</button>
            <button disabled={!!busy || !f.contact_id} onClick={() => run("pdf")} className="flex items-center justify-center gap-1.5 rounded-xl bg-slate-900 py-3 text-sm font-semibold text-white disabled:opacity-50" data-testid="crm-quote-pdf">{busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF</button>
            <button disabled={!!busy || !f.contact_id} onClick={() => run("wa")} className="flex items-center justify-center gap-1.5 rounded-xl bg-emerald-500 py-3 text-sm font-semibold text-white disabled:opacity-50" data-testid="crm-quote-whatsapp">{busy === "wa" ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />} Enviar</button>
          </div>
          {saved && <button onClick={del} className="w-full py-2 text-xs text-red-500" data-testid="crm-quote-delete">Eliminar cotización</button>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
